import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Menu,
  TextField,
  ClickAwayListener
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { FixedSizeList } from 'react-window';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { setVotingTableData } from '../../redux/FeedbackCollation/feedbackCollationSlice';
import { setDecisionVotingTableData } from '../../redux/DecisionCentral/decisionCentralRulesSlice';

const MultiSelectFilters = ({
  tableName,
  isTableLoading,
  data = [],
  filtersConfig = [],
  onApplyFilters,
  onResetFilters,
  customerFilter,
  selectedCustomer = null
}) => {
  const dispatch = useDispatch();
  const [selectedFilters, setSelectedFilters] = useState(
    filtersConfig?.reduce((acc, filter) => ({ ...acc, [filter?.key]: [] }), {})
  );

  const [searchQuery, setSearchQuery] = useState(
    filtersConfig.reduce((acc, filter) => ({ ...acc, [filter?.key]: '' }), {})
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [allSelected, setAllSelected] = useState(false);

  const getOptions = (key) => {
    let options;

    switch (key) {
      case 'region':
        options = data?.regionList?.map((region) => region.reportingRegion);
        break;
      case 'channel':
        options = data?.channelList?.map((channel) => channel.reportingChannel);
        break;
      case 'level_attribute_name':
        // Directly return customerFilter if it's an array, or convert it to array if not
        options = Array.isArray(customerFilter)
          ? customerFilter
          : customerFilter
            ? [customerFilter]
            : [];
        break;
      default:
        {
          const data1 = Array.isArray(data?.['filterList'])
            ? data['filterList']
            : Array.isArray(data)
              ? data
              : [];
          options = [
            ...new Set(
              data1
                // ?.filter(v => v?.["level_attribute_name"] !== "NATIONAL")
                ?.filter((item) =>
                  filtersConfig
                    .find((f) => f.key === key)
                    ?.dependsOn?.every((dep) =>
                      dep === 'votes aligned'
                        ? !selectedFilters[dep].length ||
                          selectedFilters[dep]?.includes(
                            item[dep] === true ? 'Yes' : item[dep] === false ? 'No' : ''
                          )
                        : !selectedFilters[dep].length || selectedFilters[dep]?.includes(item[dep])
                    )
                )
                .flatMap((item) => {
                  if (key === 'votes aligned') {
                    if (item[key] === true) return ['Yes'];
                    if (item[key] === false) return ['No'];
                    return [''];
                  }
                  return [item[key]];
                })
            )
          ];
        }
        break;
    }

    // Return the complete list of options, including selected options
    return (
      options
        ?.filter(Boolean)
        ?.map((option) => String(option))
        ?.sort((a, b) => a?.localeCompare(b)) || []
    );
  };
  // New ****
  const filteredOptions = useMemo(() => {
    return filtersConfig.reduce((acc, filter) => {
      const { key } = filter;
      const options = getOptions(filter.key);

      acc[key] = searchQuery[key]
        ? options.filter((option) =>
            option?.toLowerCase()?.includes(searchQuery[filter.key]?.toLowerCase())
          )
        : options;

      return acc;
    }, {});
  }, [data, filtersConfig, selectedFilters, searchQuery, customerFilter]);

  const handleFilterChange = (key, values) => {
    // Update the selected filters state
    setSelectedFilters((prev) => {
      const updatedFilters = { ...prev, [key]: values }; // Define updatedFilters here

      const updateChildFilters = (parentKey) => {
        const parentIndex = filtersConfig?.findIndex((f) => f.key === parentKey);

        for (let i = parentIndex + 1; i < filtersConfig.length; i++) {
          const childKey = filtersConfig?.[i]?.key;
          let dependsOn = filtersConfig?.[i]?.dependsOn;
          const dependencies = Array.isArray(selectedFilters['level_attribute_name'])
            ? [...dependsOn, ...['level_attribute_name']]
            : dependsOn;
          const data1 = data?.['filterList'] || data;
          const validOptions = [
            ...new Set(
              data1
                ?.filter((item) =>
                  dependencies?.every((dep) => {
                    if (dep === 'votes aligned') {
                      return (
                        !updatedFilters[dep]?.length ||
                        updatedFilters[dep]?.includes(
                          item[dep] === true ? 'Yes' : item?.[dep] === false ? 'No' : ''
                        )
                      );
                    }
                    return (
                      !updatedFilters[dep]?.length || updatedFilters[dep]?.includes(item?.[dep])
                    );
                  })
                )
                ?.map((item) => item?.[childKey])
            )
          ];

          updatedFilters[childKey] = updatedFilters?.[childKey]?.filter((val) =>
            validOptions?.includes(val)
          );
        }
      };

      updateChildFilters(key);
      if (
        selectedFilters['level_attribute_name'] !== null &&
        selectedFilters['level_attribute_name'] !== undefined &&
        key !== 'level_attribute_name'
      ) {
        updatedFilters['level_attribute_name'] = selectedFilters['level_attribute_name'];
      }
      return updatedFilters;
    });
  };

  const handleSearch = (key, query) => {
    setSearchQuery((prev) => ({ ...prev, [key]: query }));
  };

  const handleReset = () => {
    setSelectedFilters(filtersConfig?.reduce((acc, filter) => ({ ...acc, [filter.key]: [] }), {}));
    setSearchQuery(filtersConfig?.reduce((acc, filter) => ({ ...acc, [filter.key]: '' }), {}));

    if (onResetFilters) {
      onResetFilters();
    }
  };

  const handleApply = (filters) => {
    const filterData = Object.keys(selectedFilters)?.reduce((acc, key) => {
      acc[key] = selectedFilters?.[key]?.length === 0 ? null : selectedFilters?.[key];
      return acc;
    }, {});
    onApplyFilters(filterData);
  };

  const isAllSelected = (key, options) =>
    options?.length > 0 && selectedFilters[key]?.length === options?.length;

  const handleSelectAll = (key, options) => {
    if (isAllSelected(key, options)) {
      handleFilterChange(key, []);
      setAllSelected(false);
    } else {
      handleFilterChange(key, options);
      setAllSelected(true);
    }
  };

  const handleMenuOpen = (event, key) => {
    setAnchorEl(event.currentTarget);
    setActiveKey(key);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveKey(null);
  };

  useEffect(() => {
    if (tableName === 'voting table' || tableName === 'decision table') {
      const selectedValues = Object.keys(selectedFilters)?.reduce((acc, key) => {
        acc[key] = selectedFilters[key]?.length === 0 ? null : selectedFilters[key];
        return acc;
      }, {});
      const filteredData = [...data]?.filter((item) => {
        return filtersConfig?.every(({ key }) => {
          const selectedValuesForKey = selectedValues?.[key] || [];
          const normalizedItemValue =
            key === 'votes aligned'
              ? item[key] === true
                ? 'Yes'
                : item[key] === false
                  ? 'No'
                  : ''
              : item[key];

          return (
            selectedValuesForKey.length === 0 ||
            (Array.isArray(normalizedItemValue)
              ? selectedValuesForKey?.some((val) => normalizedItemValue.includes(val)) // Check for intersection
              : selectedValuesForKey?.includes(normalizedItemValue)) // Direct match for non-array fields
          );
        });
      });

      if (!isTableLoading && selectedCustomer !== null) {
        if (tableName === 'voting table') dispatch(setVotingTableData(filteredData));
        if (tableName === 'decision table') dispatch(setDecisionVotingTableData(filteredData));
      }
    }
  }, [isTableLoading]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        justifyContent: 'flex-start',
        alignItems: 'center'
      }}>
      {filtersConfig?.map((filter) => {
        const key = filter.key;
        const options = filteredOptions?.[key];
        const selectedValues = selectedFilters?.[key] || [];

        return (
          <Box
            key={key}
            sx={{
              display: 'inline-block',
              backgroundColor: 'transparent',
              borderRadius: '24px',
              border: '2px solid #D7E5F8',
              padding: '0.2rem',
              minWidth: 80
            }}>
            <FormControl
              variant="outlined"
              size="small"
              sx={{
                minWidth: 120,
                backgroundColor: 'transparent',
                margin: 0
              }}>
              <InputLabel
                sx={{
                  color: '#0056b3',
                  fontSize: '0.8rem',
                  transition: 'transform 0.2s, font-size 0.2s',
                  lineHeight: '0.8rem',

                  zIndex: '1'
                }}
              />
              <Button
                onClick={(event) => handleMenuOpen(event, key)}
                sx={{
                  color: '#0056b3',
                  height: '2rem',
                  fontSize: '1rem',
                  display: 'flex',
                  fontWeight: '300 !important',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0 0.4rem',
                  textTransform: 'none',
                  position: 'relative',
                  maxWidth: '250px'
                }}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {filter.label} {/* Label with truncation */}
                </span>
                <ArrowDropDownIcon sx={{ marginLeft: '5px' }} /> {/* Chevron remains intact */}
                {selectedValues?.length > 0 && (
                  <span
                    style={{
                      fontSize: '0.8rem',
                      background: '#1976d2',
                      color: '#fff',
                      borderRadius: '5px',
                      padding: '0 5px',
                      marginLeft: '5px'
                    }}>
                    {allSelected && selectedValues?.length === options.length
                      ? 'All'
                      : `+${selectedValues?.length}`}
                  </span>
                )}
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={activeKey === key}
                onClose={handleMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button'
                }}
                PaperProps={{
                  style: {
                    maxHeight: '400px',
                    width: 'max-content'
                  }
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center'
                }}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'center'
                }}
                sx={{
                  top: '2.5rem'
                }}>
                <ClickAwayListener onClickAway={handleMenuClose}>
                  <Box sx={{ padding: '1rem', minWidth: 250 }}>
                    <TextField
                      placeholder="Search..."
                      variant="standard"
                      value={searchQuery[key]}
                      onChange={(e) => handleSearch(key, e.target.value)}
                      fullWidth
                      sx={{ marginBottom: '0.5rem' }}
                    />
                    <MenuItem
                      value="selectAll"
                      onClick={() => handleSelectAll(key, options)}
                      style={{ paddingLeft: '0' }}>
                      <Checkbox
                        checked={isAllSelected(key, options)}
                        indeterminate={
                          selectedFilters[key]?.length > 0 &&
                          selectedFilters[key]?.length < options.length
                        }
                        sx={{ padding: '0 2px' }}
                      />
                      <ListItemText primary="SELECT ALL" />
                    </MenuItem>
                    {/* )} */}

                    <FixedSizeList
                      height={Math.min(250, options?.length * 35)}
                      width="100%"
                      itemSize={35}
                      itemCount={options?.length}>
                      {({ index, style }) => {
                        const option = options[index];
                        return (
                          <MenuItem
                            key={option}
                            style={{
                              ...style,
                              paddingLeft: '0',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            value={option}
                            onClick={() => {
                              handleFilterChange(
                                key,
                                selectedValues?.includes(option)
                                  ? selectedValues?.filter((val) => val !== option)
                                  : [...selectedValues, option]
                              );
                            }}>
                            <Checkbox
                              checked={selectedValues?.includes(option)}
                              sx={{ padding: '0 2px' }}
                            />
                            <ListItemText
                              primary={option}
                              primaryTypographyProps={{
                                style: {
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: '200px'
                                },
                                title: option
                              }}
                            />
                          </MenuItem>
                        );
                      }}
                    </FixedSizeList>
                  </Box>
                </ClickAwayListener>
              </Menu>
            </FormControl>
          </Box>
        );
      })}
      {/* {!isApplyButtonVisible &&  */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleApply}
        sx={{ fontSize: '1rem' }}
        style={{
          border: '1px solid #0056b3',
          borderRadius: '20px',
          background: '#0056b3',
          height: '32px'
        }}>
        Apply
      </Button>
      {/* }       */}
      <Button
        variant="outlined"
        color="secondary"
        onClick={handleReset}
        sx={{ fontSize: '1rem' }}
        style={{
          borderRadius: '20px',
          color: '#0056b3',
          border: '1px solid #0056b3',
          height: '32px'
        }}>
        Reset
      </Button>
    </Box>
  );
};

export default MultiSelectFilters;
